import { defineStrings, type Locale } from "../i18n";
import { percent, translator, unit } from "./shared";

const strings = defineStrings(
  {
    progress: "Progress",
    done: "{percent} done",
    noData: "No data yet",
  },
  {
    progress: "Progression",
    done: "{percent} atteint",
    noData: "Pas encore de données",
  },
);

export interface GoalSummaryProps {
  readonly title: string;
  readonly target: string;
  /** From the engine, 0..1, or null before there is any data. */
  readonly progress: number | null;
  readonly dueLabel?: string;
  readonly locale: Locale;
}

/** A parent's goal for the child, with a progress bar or "No data yet". */
export function GoalSummary(props: GoalSummaryProps) {
  const t = translator(strings, props.locale);
  const value = unit(props.progress);
  const shown = value === null ? null : percent(props.locale, value);
  const whole = value === null ? 0 : Math.round(value * 100);
  return (
    <section className="p-goal" aria-label={props.title}>
      <p className="p-goal__title">{props.title}</p>
      <p className="p-goal__target">{props.target}</p>
      {shown === null ? (
        <p className="p-goal__empty">{t("noData")}</p>
      ) : (
        <div className="p-goal__progress">
          <div
            className="p-meter"
            role="meter"
            aria-label={`${props.title}, ${t("progress")}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={whole}
            aria-valuetext={t("done", { percent: shown })}
          >
            <span className="p-meter__fill" style={{ inlineSize: `${String(whole)}%` }} />
          </div>
          <span className="p-goal__value" aria-hidden="true">
            {shown}
          </span>
        </div>
      )}
      {props.dueLabel !== undefined && <p className="p-goal__due">{props.dueLabel}</p>}
    </section>
  );
}
